<template>
  <nav
    class="flex bg-surface-0 dark:bg-surface-900 p-2 rounded-lg shadow-sm shadow-surface-200/50 dark:shadow-none"
    aria-label="Breadcrumb"
  >
    <ol class="flex items-center space-x-2 text-sm text-surface-600 dark:text-surface-300">
      <li
        v-for="(item, index) in breadcrumbItems"
        :key="item.name"
        class="flex items-center gap-1"
      >
        <template v-if="index > 0">
          <span class="mx-2">/</span>
        </template>
        <div class="flex items-center gap-1">
          <component v-if="item.icon" :is="item.icon" class="w-4 h-4" />
          <router-link
            v-if="item.to && index < breadcrumbItems.length - 1"
            :to="item.to"
            class="hover:text-rch-black dark:hover:text-surface-300 underline-0 hover:underline underline-offset-2"
          >
            {{ item.label }}
          </router-link>
          <span
            v-else
            :class="
              index === breadcrumbItems.length - 1
                ? 'text-surface-900 dark:text-surface-0 font-medium'
                : ''
            "
          >
            {{ item.label }}
          </span>
        </div>
      </li>
    </ol>
  </nav>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { HomeIcon } from '@heroicons/vue/24/outline'
import { useFCCMerchantStore } from '@/stores/fcc'
import { useMerchantConfigurationStore } from '@/stores/merchantConfigurationStore'
import { useAvalaraManagementStore } from '@/stores/avalaraManagementStore'

interface BreadcrumbItem {
  name: string
  label: string
  to?: string
  icon?: any
}

const route = useRoute()
const router = useRouter()
const merchantStore = useFCCMerchantStore()
const merchantConfigStore = useMerchantConfigurationStore()
const avalaraStore = useAvalaraManagementStore()

function getParamNamesFromPath(path: string): string[] {
  return path
    .split('/')
    .filter(segment => segment.startsWith(':'))
    .map(segment => segment.slice(1))
}

const breadcrumbItems = computed((): BreadcrumbItem[] => {
  const items: BreadcrumbItem[] = []

  // Always start with Home
  items.push({
    name: 'home',
    label: 'Home',
    to: '/',
    icon: HomeIcon,
  })

  // Build breadcrumb from matched routes
  const matchedRoutes = route.matched.filter(route => route.meta?.displayName)

  matchedRoutes.forEach((matchedRoute, index) => {
    const isLast = index === matchedRoutes.length - 1
    const displayName = matchedRoute.meta?.displayName as string

    if (displayName) {
      // Use the icon directly from route meta
      const icon = matchedRoute.meta?.icon

      // Generate proper route path for navigation.
      // Routes flagged disableBreadcrumbLink (e.g. redirect-only parents with no
      // landing page) render as plain text instead of a link.
      let routePath = undefined
      if (!isLast && !matchedRoute.meta?.disableBreadcrumbLink) {
        try {
          // Try to resolve the route with only the params that this route expects
          const expectedParamNames = getParamNamesFromPath(matchedRoute.path)
          const paramsForRoute = expectedParamNames.length
            ? Object.fromEntries(
                Object.entries(route.params).filter(([key]) =>
                  expectedParamNames.includes(key),
                ),
              )
            : undefined
          const resolved = router.resolve({
            name: matchedRoute.name as string,
            params: paramsForRoute as any,
          })
          routePath = resolved.path
        } catch {
          // Fallback to the route path if resolution fails
          routePath = matchedRoute.path
        }
      }

      // Enhance label for specific routes (e.g., show merchant name on details)
      let labelText = displayName
      if ((matchedRoute.name as string) === 'fccMerchantDetails') {
        const name = merchantStore.currentMerchantName
        const fallbackId = (route.params as any)?.id
        if (name) {
          labelText = `${displayName}: ${name}`
        } else if (fallbackId) {
          labelText = `${displayName}: ${fallbackId}`
        }
      }
      if ((matchedRoute.name as string) === 'merchantConfiguration') {
        const merchant = merchantConfigStore.currentMerchant
        if (merchant?.Name) {
          labelText = merchant.Name
        } else {
          const fallbackId = (route.params as any)?.id
          if (fallbackId) {
            labelText = fallbackId
          }
        }
      }
      if ((matchedRoute.name as string) === 'avalaraCompanyDetail') {
        const companyId = (route.params as Record<string, string>)?.companyId
        if (companyId) {
          const idNum = Number(companyId)
          // The list store is only populated after visiting the companies list,
          // so on a direct deep link fall back to the detail-page fetches
          // (overview / nexus detail), id-matched to avoid showing a stale name
          // from a previously viewed company.
          const fromList = avalaraStore.companies.find(
            c => c.id === idNum,
          )?.name
          const fromDetail =
            avalaraStore.companyOverview?.id === idNum
              ? avalaraStore.companyOverview.name
              : avalaraStore.companyNexusDetail?.id === idNum
                ? avalaraStore.companyNexusDetail.name
                : undefined
          labelText = fromList ?? fromDetail ?? `Company ${companyId}`
        }
      }
      items.push({
        name: matchedRoute.name as string,
        label: labelText,
        to: routePath,
        icon,
      })
    }
  })

  return items
})
</script>
